import { useCallback, useState, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import "react-toastify/dist/ReactToastify.css";
import NaoEncontrado from "./telas/NaoEncontrado";
import SolicitacaoCaixa from "./telas/SolicitacaoCaixa";
import AtualizaSolicitacaoCaixa from "./telas/AtualizaSolicitacaoCaixa";
import ResetarSenha from "./telas/ResetarSenha";
import EsqueceuSenha from "./telas/EsqueceuSenha";

import Login from "./telas/Login";
import BarraNavegacao from "./components/BarraNavegacao";
import Usuario from "./telas/Usuario";
import Profissional from "./telas/Profissional";
import Especialidade from "./telas/Especialidade";
import FormaPagamento from "./telas/FormaPagamento";
import Fornecedor from "./telas/Fornecedor";
import UnidadeControle from "./telas/UnidadeControle";
import Produto from "./telas/Produto";
import ListaCompras from "./telas/ListaCompras";
import InteligenciaEstoque from "./telas/InteligenciaEstoque";
import EntradaProdutos from "./telas/EntradaProdutos";
import SaidasEstoque from "./telas/SaidasEstoque";
import PerdasAvarias from "./telas/PerdasAvarias";
import RelatoriosEstoque from "./telas/RelatoriosEstoque";
import GerenciamentoImpedimentos from "./telas/GerenciamentoImpedimentos";
import CaixaDiario from "./telas/CaixaDiario";
import CaixaGeral from "./telas/CaixaGeral";
import FolhaPagamento from "./telas/FolhaPagamento";
import Despesa from "./telas/Despesa";
import Grupo from "./telas/Grupo";
import Organizacao from "./telas/Organizacao";
import Subgrupo from "./telas/Subgrupo";
import {
  authService,
  setAuthErrorCallback,
  setLogoutCallback,
} from "./services/apiService";
import GraficoCaixaGeral from "./telas/GraficoCaixaGeral";
import { useNotification } from "./hooks/useNotification";
import TipoCaixa from "./telas/TipoCaixa";
import ConfiguracaoCaixaSisodonto from "./telas/ConfiguracaoCaixaSisodonto";
import ApuracaoMensalFundos from "./telas/ApuracaoMensalFundos";
import DashboardDespesa from "./telas/DashboardDespesa";
import EntradaCaixa from "./telas/EntradaCaixa";
import ResumoProfissional from "./telas/ResumoProfissional";
import ComparativoFinanceiro from "./telas/ComparativoFinanceiro";
import MaquinaCartao from "./telas/MaquinaCartao";
import Socio from "./telas/Socio";
import FundoReserva from "./telas/FundoReserva";
import MovimentoFundoReserva from "./telas/MovimentoFundoReserva";
import ContaBancaria from "./telas/ContaBancaria";
import { ConfirmDialogProvider } from "./hooks/useConfirmDialog";
import { SessaoContext } from "./context/SessaoContext";
import BotaoAjuda from "./ajuda/BotaoAjuda";
import MeuPonto from "./telas/MeuPonto";
import GestaoPonto from "./telas/GestaoPonto";

// Componente de Loading
const Loading = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Carregando sistema...</p>
    </div>
  </div>
);

// Componente de Rota Protegida
export const ProtectedRoute = ({
  children,
  requiredRole,
  user,
  sessaoOrganizacao,
}) => {
  const sessao = sessaoOrganizacao || authService.getSessaoOrganizacao();
  if (!sessao?.organizacaoId) {
    return <Navigate to="/login" replace />;
  }
  const rolesPermitidos = Array.isArray(requiredRole)
    ? requiredRole
    : [requiredRole];
  if (
    requiredRole &&
    !rolesPermitidos.some((role) => authService.hasRole(user, role, sessao))
  ) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const queryClient = useQueryClient();
  const [autenticado, setAutenticado] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [sessaoOrganizacao, setSessaoOrganizacao] = useState(null);
  const [sidebarExpandida, setSidebarExpandida] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1050);
  const { showInfo } = useNotification();

  const resolverSessaoOrganizacaoInicial = (sessaoOrg) => {
    if (!sessaoOrg || !Array.isArray(sessaoOrg.organizacoes)) {
      return null;
    }
    if (sessaoOrg.organizacoes.length === 0) {
      return null;
    }

    if (sessaoOrg.organizacaoId) {
      const organizacaoAtiva = sessaoOrg.organizacoes.find(
        (org) => org.id === sessaoOrg.organizacaoId,
      );
      if (organizacaoAtiva) {
        return {
          ...sessaoOrg,
          permissaoOrganizacao:
            organizacaoAtiva.permissao || sessaoOrg.permissaoOrganizacao,
          ultimaOrganizacaoSelecionada:
            sessaoOrg.ultimaOrganizacaoSelecionada || organizacaoAtiva.id,
        };
      }
    }

    if (sessaoOrg.ultimaOrganizacaoSelecionada) {
      const ultima = sessaoOrg.organizacoes.find(
        (org) => org.id === sessaoOrg.ultimaOrganizacaoSelecionada,
      );
      if (ultima) {
        return {
          ...sessaoOrg,
          organizacaoId: ultima.id,
          permissaoOrganizacao: ultima.permissao,
        };
      }
    }

    const primeira = sessaoOrg.organizacoes[0];
    return {
      ...sessaoOrg,
      organizacaoId: primeira.id,
      permissaoOrganizacao: primeira.permissao,
      ultimaOrganizacaoSelecionada: primeira.id,
    };
  };

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1050);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const verificarAutenticacao = async () => {
      try {
        const token = localStorage.getItem("token");

        if (token) {
          const dadosUsuario = authService.getCurrentUser();
          const sessaoOrg = resolverSessaoOrganizacaoInicial(
            authService.getSessaoOrganizacao(),
          );
          if (dadosUsuario && sessaoOrg?.organizacaoId) {
            setUsuario(dadosUsuario);
            setSessaoOrganizacao(sessaoOrg);
            authService.setSessaoOrganizacao(sessaoOrg);
            setAutenticado(true);
          }
        }
      } catch (erro) {
        showInfo(erro.message);
        authService.logout();
      } finally {
        setCarregando(false);
      }
    };
    setLogoutCallback(() => {
      tratarLogout();
    });
    setAuthErrorCallback(({ type, message }) => {
      if (type === "organization_scope") {
        showInfo(message || "Sessão sem escopo de organização válido.");
        return;
      }
      showInfo(message || "Sessão inválida.");
      tratarLogout();
    });

    verificarAutenticacao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tratarLogin = (dadosUsuario, token, sessaoOrg) => {
    const sessaoNormalizada = resolverSessaoOrganizacaoInicial(sessaoOrg);
    if (!dadosUsuario || !token || !sessaoNormalizada) return;

    queryClient.clear();
    localStorage.setItem("token", token);

    // Salvar dados completos do usuário
    const usuarioCompleto = {
      id: dadosUsuario.id,
      nome: dadosUsuario.nome,
      email: dadosUsuario.email,
      role: dadosUsuario.role,
      authorities: dadosUsuario.authorities,
      ativo: dadosUsuario.ativo !== undefined ? dadosUsuario.ativo : true,
      permissao: dadosUsuario.permissao || dadosUsuario.role,
      createdAt: dadosUsuario.createdAt,
    };

    localStorage.setItem("userData", JSON.stringify(usuarioCompleto));
    authService.setSessaoOrganizacao(sessaoNormalizada);
    setUsuario(usuarioCompleto);
    setSessaoOrganizacao(sessaoNormalizada);
    setAutenticado(true);
  };

  const tratarLogout = () => {
    queryClient.clear();
    authService.logout();
    setUsuario(null);
    setSessaoOrganizacao(null);
    setAutenticado(false);
    setSidebarExpandida(true);
  };

  const tratarTrocaOrganizacao = useCallback(
    async (organizacaoId) => {
      const resultado = await authService.trocarOrganizacao(organizacaoId);
      queryClient.clear();
      if (resultado?.sessaoOrganizacao) {
        setSessaoOrganizacao(resultado.sessaoOrganizacao);
      }
      if (resultado?.user) {
        setUsuario(resultado.user);
      }
      window.dispatchEvent(new Event("organizacao-changed"));
    },
    [queryClient],
  );

  const tratarToggleSidebar = (expandido) => {
    setSidebarExpandida(expandido);
  };

  const possuiPermissao = (nomePermissao) => {
    return authService.hasRole(usuario, nomePermissao, sessaoOrganizacao);
  };

  const tratarAtualizacaoUsuario = (usuarioAtualizado) => {
    const dadosAtualizados = {
      ...usuario,
      ...usuarioAtualizado,
    };

    localStorage.setItem("userData", JSON.stringify(dadosAtualizados));
    setUsuario(dadosAtualizados);
  };

  const sessaoContextValue = useMemo(
    () => ({
      usuario,
      sessaoOrganizacao,
      organizacaoAtiva:
        sessaoOrganizacao?.organizacoes?.find(
          (o) => o.id === sessaoOrganizacao?.organizacaoId,
        ) || null,
      trocarOrganizacao: tratarTrocaOrganizacao,
    }),
    [usuario, sessaoOrganizacao, tratarTrocaOrganizacao],
  );

  if (carregando) {
    return <Loading />;
  }

  return (
    <ConfirmDialogProvider>
      <SessaoContext.Provider value={sessaoContextValue}>
        <Router future={{ v7_startTransition: true }}>
          <div className="flex h-screen overflow-hidden bg-gray-50">
            {autenticado && (
              <BarraNavegacao
                usuario={usuario}
                onLogout={tratarLogout}
                isOpen={sidebarExpandida}
                onToggle={tratarToggleSidebar}
                possuiPermissao={possuiPermissao}
                onUserUpdate={tratarAtualizacaoUsuario}
                sessaoOrganizacao={sessaoOrganizacao}
                onTrocarOrganizacao={tratarTrocaOrganizacao}
              />
            )}

            <main
              className={`flex-1 min-w-0 min-h-0 overflow-y-auto ${
                autenticado
                  ? `bg-gray-50 ${isMobile ? "pb-20" : ""}`
                  : "bg-gradient-to-br from-blue-50 to-indigo-100"
              }`}
            >
              <Routes
                key={sessaoOrganizacao?.organizacaoId || "sem-organizacao"}
              >
                {/* Rota de Login */}
                <Route
                  path="/login"
                  element={
                    !autenticado ? (
                      <Login onLogin={tratarLogin} />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                {/* Caixa Diário - Tela Principal */}
                <Route
                  path="/"
                  element={
                    autenticado ? (
                      <CaixaDiario usuario={usuario} />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Rotas Admin */}
                <Route
                  path="/organizacoes"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="super_admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <Organizacao />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/usuarios"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <Usuario />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/tipo-caixa"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <TipoCaixa />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/configuracao-caixa-sisodonto"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <ConfiguracaoCaixaSisodonto />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/fundo-reserva"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <FundoReserva />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/movimento-fundo-reserva"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <MovimentoFundoReserva />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/profissionais"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Profissional />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/especialidades"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Especialidade />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/formas-pagamento"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <FormaPagamento />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/fornecedores"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <Fornecedor />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/unidades-controle"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <UnidadeControle />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/produtos"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <Produto />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/lista-compras"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <ListaCompras />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/avaliar-estoque"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <InteligenciaEstoque />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/entrada-produtos"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <EntradaProdutos />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/entradas-manuais"
                  element={
                    <Navigate to="/entrada-produtos?modo=pedido" replace />
                  }
                />
                <Route
                  path="/saida-estoque"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <SaidasEstoque />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/saidas-estoque"
                  element={<Navigate to="/saida-estoque" replace />}
                />
                <Route
                  path="/saidas-produtos"
                  element={<Navigate to="/saida-estoque" replace />}
                />
                <Route
                  path="/entradas-notas"
                  element={
                    <Navigate to="/entrada-produtos?modo=nota-fiscal" replace />
                  }
                />
                <Route
                  path="/relatorios"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <RelatoriosEstoque />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/perdas-avarias"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole={["admin", "user"]}
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <PerdasAvarias />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/despesas"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Despesa />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/grupos"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Grupo />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/subgrupos"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Subgrupo />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/impedimento"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <GerenciamentoImpedimentos />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/caixa-geral"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <CaixaGeral />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/folha-pagamento"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <FolhaPagamento />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                <Route
                  path="/solicitacao-caixa"
                  element={
                    autenticado ? (
                      <SolicitacaoCaixa />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Solicitação Reabertura Admin - ADMIN */}
                <Route
                  path="/solicitacao-caixa-admin"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <AtualizaSolicitacaoCaixa />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/grafico-caixa"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <GraficoCaixaGeral />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/apuracao-mensal-fundos"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <ApuracaoMensalFundos />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard-despesas"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <DashboardDespesa />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/entrada-caixa"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <EntradaCaixa />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/resumo-profissional"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <ResumoProfissional />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/comparativo-financeiro"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <ComparativoFinanceiro />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/maquina-cartao"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <MaquinaCartao />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/socios"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <Socio />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/conta-bancaria"
                  element={
                    autenticado ? (
                      <ProtectedRoute requiredRole="admin" user={usuario}>
                        <ContaBancaria />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/meu-ponto"
                  element={
                    autenticado ? (
                      <MeuPonto />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/gestao-ponto"
                  element={
                    autenticado ? (
                      <ProtectedRoute
                        requiredRole="admin"
                        user={usuario}
                        sessaoOrganizacao={sessaoOrganizacao}
                      >
                        <GestaoPonto />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route path="/esqueceu-senha" element={<EsqueceuSenha />} />
                <Route
                  path="/redefinir-senha/:token"
                  element={<ResetarSenha />}
                />
                {/* Rota 404 - redireciona para tela principal */}
                <Route path="*" element={<NaoEncontrado />} />
              </Routes>
            </main>
          </div>
          {autenticado && <BotaoAjuda />}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </Router>
      </SessaoContext.Provider>
    </ConfirmDialogProvider>
  );
}

export default App;
